/**
 * Calendar React Query Hooks
 *
 * Supports 3 event types:
 * - Event: Pure events (live/telematic), with recurrence and participant reminders
 * - Reminder: Linked to a task, follows task recurrence
 * - Milestone: Linked to a project, groups tasks
 *
 * For non-React contexts, use getCalendarEventsFromCache().
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { graphqlFetch } from '../database/graphqlClient';
import { queryKeys } from './queryKeys';
import { queryClient } from './queryClient';
import type { ISODate } from '../domain/models';

// =============================================================================
// TYPES
// =============================================================================

export type EventType = 'event' | 'reminder' | 'milestone';
export type EventMode = 'live' | 'call' | 'videocall';
export type NotificationChannel = 'email' | 'whatsapp' | 'telegram' | 'sms' | 'push';
export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';
export type ParticipantStatus = 'pending' | 'accepted' | 'declined';

/**
 * Reminder configuration for notifications
 */
export interface ReminderConfig {
  channel: NotificationChannel;
  offsetMinutes: number;
  sent?: boolean;
  sentAt?: ISODate;
}

/**
 * Event participant with their own reminder settings
 */
export interface EventParticipant {
  userId?: string;
  contactId?: string;
  email?: string;
  displayName?: string;
  status: ParticipantStatus;
  reminders: ReminderConfig[];
}

/**
 * Recurrence rule for events
 */
export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval: number;
  byDay: number[];
  byMonthDay?: number;
  until?: ISODate;
  count?: number;
}

/**
 * Calendar Event - supports 3 types: event, reminder, milestone
 */
export interface CalendarEvent {
  id: string;
  eventType: EventType;
  title: string;
  description?: string;
  startAt: ISODate;
  endAt?: ISODate;
  allDay: boolean;
  timezone?: string;
  // Event-specific
  mode?: EventMode;
  location?: string;
  meetingUrl?: string;
  recurrence?: RecurrenceRule;
  organizerReminders: ReminderConfig[];
  participants: EventParticipant[];
  // Reminder-specific (linked to task)
  linkedTaskId?: string;
  // Milestone-specific (linked to project and tasks)
  projectId?: string;
  linkedTaskIds: string[];
  // Common links
  linkedEntityId?: string;
  linkedEntityType?: string;
  contactId?: string;
  color?: string;
  // Metadata
  createdBy: string;
  createdAt: ISODate;
  updatedAt: ISODate;
  status: string;
}

/**
 * Legacy Reminder type (for backwards compatibility)
 * @deprecated Use CalendarEvent with eventType='reminder' instead
 */
export interface Reminder {
  id: string;
  title: string;
  description?: string;
  at: ISODate;
  done: boolean;
  doneAt?: ISODate;
  linkedToId?: string;
  linkedToType?: 'event' | 'task' | 'milestone' | 'invoice' | 'contact';
  priority?: 'low' | 'medium' | 'high';
  createdAt: ISODate;
  updatedAt: ISODate;
}

// =============================================================================
// GRAPHQL
// =============================================================================

const CALENDAR_EVENTS_QUERY = `
  query GetCalendarEvents($eventType: String, $projectId: String, $fromDate: String, $toDate: String) {
    calendarEvents(eventType: $eventType, projectId: $projectId, fromDate: $fromDate, toDate: $toDate) {
      id
      eventType
      title
      description
      startAt
      endAt
      allDay
      timezone
      mode
      location
      meetingUrl
      recurrence {
        frequency
        interval
        byDay
        byMonthDay
        until
        count
      }
      organizerReminders {
        channel
        offsetMinutes
        sent
        sentAt
      }
      participants {
        userId
        contactId
        email
        displayName
        status
        reminders {
          channel
          offsetMinutes
          sent
          sentAt
        }
      }
      linkedTaskId
      projectId
      linkedTaskIds
      linkedEntityId
      linkedEntityType
      color
      createdBy
      createdAt
      updatedAt
      status
    }
  }
`;

const CALENDAR_EVENT_QUERY = `
  query GetCalendarEvent($id: String!) {
    calendarEvent(id: $id) {
      id
      eventType
      title
      description
      startAt
      endAt
      allDay
      timezone
      mode
      location
      meetingUrl
      recurrence {
        frequency
        interval
        byDay
        byMonthDay
        until
        count
      }
      organizerReminders {
        channel
        offsetMinutes
        sent
        sentAt
      }
      participants {
        userId
        contactId
        email
        displayName
        status
        reminders {
          channel
          offsetMinutes
          sent
          sentAt
        }
      }
      linkedTaskId
      projectId
      linkedTaskIds
      linkedEntityId
      linkedEntityType
      color
      createdBy
      createdAt
      updatedAt
      status
    }
  }
`;

const CREATE_CALENDAR_EVENT_MUTATION = `
  mutation CreateCalendarEvent($input: CreateCalendarEventInput!) {
    createCalendarEvent(input: $input) {
      id
      eventType
      title
      description
      startAt
      endAt
      allDay
      timezone
      mode
      location
      meetingUrl
      recurrence {
        frequency
        interval
        byDay
        byMonthDay
        until
        count
      }
      organizerReminders {
        channel
        offsetMinutes
      }
      participants {
        userId
        contactId
        email
        displayName
        status
        reminders {
          channel
          offsetMinutes
        }
      }
      linkedTaskId
      projectId
      linkedTaskIds
      color
      createdBy
      createdAt
      updatedAt
      status
    }
  }
`;

const UPDATE_CALENDAR_EVENT_MUTATION = `
  mutation UpdateCalendarEvent($id: String!, $input: UpdateCalendarEventInput!) {
    updateCalendarEvent(id: $id, input: $input) {
      id
      eventType
      title
      description
      startAt
      endAt
      allDay
      timezone
      mode
      location
      meetingUrl
      recurrence {
        frequency
        interval
        byDay
        byMonthDay
        until
        count
      }
      organizerReminders {
        channel
        offsetMinutes
      }
      participants {
        userId
        contactId
        email
        displayName
        status
        reminders {
          channel
          offsetMinutes
        }
      }
      linkedTaskId
      projectId
      linkedTaskIds
      color
      createdBy
      createdAt
      updatedAt
      status
    }
  }
`;

const DELETE_CALENDAR_EVENT_MUTATION = `
  mutation DeleteCalendarEvent($id: String!) {
    deleteCalendarEvent(id: $id)
  }
`;

// Legacy mutations for old Reminder type
const REMINDERS_QUERY = `
  query GetReminders {
    reminders {
      id
      title
      description
      at
      done
      doneAt
      linkedToId
      linkedToType
      priority
      createdAt
      updatedAt
    }
  }
`;

const CREATE_REMINDER_MUTATION = `
  mutation CreateReminder($input: ReminderInput!) {
    createReminder(input: $input) {
      id
      title
      description
      at
      done
      doneAt
      linkedToId
      linkedToType
      priority
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_REMINDER_MUTATION = `
  mutation UpdateReminder($id: String!, $input: ReminderInput!) {
    updateReminder(id: $id, input: $input) {
      id
      title
      description
      at
      done
      doneAt
      linkedToId
      linkedToType
      priority
      createdAt
      updatedAt
    }
  }
`;

const DELETE_REMINDER_MUTATION = `
  mutation DeleteReminder($id: String!) {
    deleteReminder(id: $id)
  }
`;

// =============================================================================
// MAPPERS
// =============================================================================

function mapGraphQLEvent(e: any): CalendarEvent {
  return {
    id: e.id,
    eventType: (e.eventType?.toLowerCase() || 'event') as EventType,
    title: e.title,
    description: e.description,
    startAt: e.startAt || e.startDate, // Support both new and old field names
    endAt: e.endAt || e.endDate,
    allDay: e.allDay ?? false,
    timezone: e.timezone,
    mode: e.mode?.toLowerCase() as EventMode | undefined,
    location: e.location,
    meetingUrl: e.meetingUrl,
    recurrence: e.recurrence ? {
      frequency: e.recurrence.frequency?.toLowerCase() as RecurrenceFrequency,
      interval: e.recurrence.interval || 1,
      byDay: e.recurrence.byDay || [],
      byMonthDay: e.recurrence.byMonthDay,
      until: e.recurrence.until,
      count: e.recurrence.count,
    } : undefined,
    organizerReminders: (e.organizerReminders || []).map((r: any) => ({
      channel: r.channel?.toLowerCase() as NotificationChannel,
      offsetMinutes: r.offsetMinutes || 0,
      sent: r.sent ?? false,
      sentAt: r.sentAt,
    })),
    participants: (e.participants || []).map((p: any) => ({
      userId: p.userId,
      contactId: p.contactId,
      email: p.email,
      displayName: p.displayName,
      status: (p.status?.toLowerCase() || 'pending') as ParticipantStatus,
      reminders: (p.reminders || []).map((r: any) => ({
        channel: r.channel?.toLowerCase() as NotificationChannel,
        offsetMinutes: r.offsetMinutes || 0,
        sent: r.sent ?? false,
        sentAt: r.sentAt,
      })),
    })),
    linkedTaskId: e.linkedTaskId,
    projectId: e.projectId,
    linkedTaskIds: e.linkedTaskIds || [],
    linkedEntityId: e.linkedEntityId,
    linkedEntityType: e.linkedEntityType,
    contactId: e.contactId,
    color: e.color,
    createdBy: e.createdBy || '',
    createdAt: e.createdAt,
    updatedAt: e.updatedAt,
    status: e.status || 'active',
  };
}

function mapGraphQLReminder(r: any): Reminder {
  return {
    id: r.id,
    title: r.title,
    description: r.description,
    at: r.at,
    done: r.done ?? false,
    doneAt: r.doneAt,
    linkedToId: r.linkedToId,
    linkedToType: r.linkedToType,
    priority: r.priority,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

function eventToInput(event: Partial<CalendarEvent>) {
  return {
    eventType: event.eventType?.toUpperCase() || 'EVENT',
    title: event.title,
    description: event.description || null,
    startAt: event.startAt,
    endAt: event.endAt || null,
    allDay: event.allDay ?? false,
    timezone: event.timezone || null,
    mode: event.mode?.toUpperCase() || null,
    location: event.location || null,
    meetingUrl: event.meetingUrl || null,
    recurrence: event.recurrence ? {
      frequency: event.recurrence.frequency.toUpperCase(),
      interval: event.recurrence.interval,
      byDay: event.recurrence.byDay,
      byMonthDay: event.recurrence.byMonthDay || null,
      until: event.recurrence.until || null,
      count: event.recurrence.count || null,
    } : null,
    organizerReminders: event.organizerReminders?.map(r => ({
      channel: r.channel.toUpperCase(),
      offsetMinutes: r.offsetMinutes,
    })) || [],
    participants: event.participants?.map(p => ({
      userId: p.userId || null,
      contactId: p.contactId || null,
      email: p.email || null,
      displayName: p.displayName || null,
      status: p.status?.toUpperCase() || 'PENDING',
      reminders: p.reminders?.map(r => ({
        channel: r.channel.toUpperCase(),
        offsetMinutes: r.offsetMinutes,
      })) || [],
    })) || [],
    linkedTaskId: event.linkedTaskId || null,
    projectId: event.projectId || null,
    linkedTaskIds: event.linkedTaskIds || [],
    linkedEntityId: event.linkedEntityId || null,
    linkedEntityType: event.linkedEntityType || null,
    color: event.color || null,
  };
}

function reminderToInput(reminder: Partial<Reminder>) {
  return {
    title: reminder.title,
    description: reminder.description || null,
    at: reminder.at,
    done: reminder.done ?? false,
    done_at: reminder.doneAt || null,
    linked_to_id: reminder.linkedToId || null,
    linked_to_type: reminder.linkedToType || null,
    priority: reminder.priority || null,
  };
}

// =============================================================================
// HOOKS - QUERIES
// =============================================================================

export interface CalendarEventsFilters {
  eventType?: EventType;
  projectId?: string;
  fromDate?: string;
  toDate?: string;
}

/**
 * Fetch calendar events from the server
 */
export function useCalendarEvents(filters?: CalendarEventsFilters) {
  return useQuery({
    queryKey: queryKeys.calendar.events(filters),
    queryFn: async () => {
      const response = await graphqlFetch<{ calendarEvents: any[] }>(
        CALENDAR_EVENTS_QUERY,
        {
          eventType: filters?.eventType?.toUpperCase() || null,
          projectId: filters?.projectId || null,
          fromDate: filters?.fromDate || null,
          toDate: filters?.toDate || null,
        }
      );
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (response.data?.calendarEvents) {
        return response.data.calendarEvents.map(mapGraphQLEvent);
      }
      return [];
    },
    staleTime: 5 * 60_000, // 5 minutes
  });
}

/**
 * Fetch a single calendar event by ID
 */
export function useCalendarEvent(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.calendar.event(id || ''),
    queryFn: async () => {
      if (!id) return null;
      const response = await graphqlFetch<{ calendarEvent: any }>(
        CALENDAR_EVENT_QUERY,
        { id }
      );
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (response.data?.calendarEvent) {
        return mapGraphQLEvent(response.data.calendarEvent);
      }
      return null;
    },
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

/**
 * Fetch reminders from the server (legacy)
 * @deprecated Use useCalendarEvents with eventType='reminder' instead
 */
export function useReminders() {
  return useQuery({
    queryKey: queryKeys.calendar.reminders(),
    queryFn: async () => {
      const response = await graphqlFetch<{ reminders: any[] }>(REMINDERS_QUERY);
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (response.data?.reminders) {
        return response.data.reminders.map(mapGraphQLReminder);
      }
      return [];
    },
    staleTime: 5 * 60_000,
  });
}

// =============================================================================
// HOOKS - MUTATIONS (EVENTS)
// =============================================================================

export type CreateEventInput = Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'status'>;

/**
 * Create a new calendar event
 */
export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateEventInput) => {
      const input = eventToInput(data);
      const response = await graphqlFetch<{ createCalendarEvent: any }>(
        CREATE_CALENDAR_EVENT_MUTATION,
        { input }
      );
      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (!response.data?.createCalendarEvent) {
        throw new Error('Failed to create calendar event');
      }
      return mapGraphQLEvent(response.data.createCalendarEvent);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}

/**
 * Update an existing calendar event
 */
export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CalendarEvent> }) => {
      const input = eventToInput(updates);
      const response = await graphqlFetch<{ updateCalendarEvent: any }>(
        UPDATE_CALENDAR_EVENT_MUTATION,
        { id, input }
      );
      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (!response.data?.updateCalendarEvent) {
        throw new Error('Failed to update calendar event');
      }
      return mapGraphQLEvent(response.data.updateCalendarEvent);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}

/**
 * Delete a calendar event
 */
export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await graphqlFetch<{ deleteCalendarEvent: boolean }>(
        DELETE_CALENDAR_EVENT_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (!response.data?.deleteCalendarEvent) {
        throw new Error('Failed to delete calendar event');
      }
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}

// =============================================================================
// HOOKS - MUTATIONS (LEGACY REMINDERS)
// =============================================================================

/**
 * Create a new reminder (legacy)
 * @deprecated Use useCreateEvent with eventType='reminder' instead
 */
export function useCreateReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Omit<Reminder, 'id' | 'createdAt' | 'updatedAt' | 'done'>) => {
      const input = reminderToInput({ ...data, done: false });
      const response = await graphqlFetch<{ createReminder: any }>(
        CREATE_REMINDER_MUTATION,
        { input }
      );
      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (!response.data?.createReminder) {
        throw new Error('Failed to create reminder');
      }
      return mapGraphQLReminder(response.data.createReminder);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.calendar.reminders() });
    },
  });
}

/**
 * Update an existing reminder (legacy)
 * @deprecated Use useUpdateEvent instead
 */
export function useUpdateReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Reminder> }) => {
      const input = reminderToInput(updates);
      const response = await graphqlFetch<{ updateReminder: any }>(
        UPDATE_REMINDER_MUTATION,
        { id, input }
      );
      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (!response.data?.updateReminder) {
        throw new Error('Failed to update reminder');
      }
      return mapGraphQLReminder(response.data.updateReminder);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.calendar.reminders() });
    },
  });
}

/**
 * Delete a reminder (legacy)
 * @deprecated Use useDeleteEvent instead
 */
export function useDeleteReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await graphqlFetch<{ deleteReminder: boolean }>(
        DELETE_REMINDER_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (!response.data?.deleteReminder) {
        throw new Error('Failed to delete reminder');
      }
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.calendar.reminders() });
    },
  });
}

/**
 * Mark a reminder as done (legacy)
 * @deprecated Use useUpdateEvent instead
 */
export function useMarkReminderDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const now = new Date().toISOString();
      const input = reminderToInput({ done: true, doneAt: now });
      const response = await graphqlFetch<{ updateReminder: any }>(
        UPDATE_REMINDER_MUTATION,
        { id, input }
      );
      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (!response.data?.updateReminder) {
        throw new Error('Failed to mark reminder as done');
      }
      return mapGraphQLReminder(response.data.updateReminder);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.calendar.reminders() });
    },
  });
}

// =============================================================================
// PURE SELECTOR FUNCTIONS (work on arrays)
// =============================================================================

/**
 * Get events for a specific date
 */
export function getEventsForDate(events: CalendarEvent[], date: string): CalendarEvent[] {
  const targetDate = date.split('T')[0];
  return events.filter((e) => {
    const eventDate = e.startAt.split('T')[0];
    return eventDate === targetDate;
  });
}

/**
 * Get events within a date range
 */
export function getEventsForRange(
  events: CalendarEvent[],
  startDate: string,
  endDate: string
): CalendarEvent[] {
  return events.filter((e) => {
    const eventDate = e.startAt.split('T')[0];
    return eventDate >= startDate && eventDate <= endDate;
  });
}

/**
 * Get events by type
 */
export function getEventsByType(events: CalendarEvent[], eventType: EventType): CalendarEvent[] {
  return events.filter((e) => e.eventType === eventType);
}

/**
 * Get events by project
 */
export function getEventsByProject(events: CalendarEvent[], projectId: string): CalendarEvent[] {
  return events.filter((e) => e.projectId === projectId);
}

/**
 * Get milestones for a project
 */
export function getMilestonesForProject(events: CalendarEvent[], projectId: string): CalendarEvent[] {
  return events.filter((e) => e.eventType === 'milestone' && e.projectId === projectId);
}

/**
 * Get reminders linked to a task
 */
export function getRemindersForTask(events: CalendarEvent[], taskId: string): CalendarEvent[] {
  return events.filter((e) => e.eventType === 'reminder' && e.linkedTaskId === taskId);
}

/**
 * Get events for today
 */
export function getTodayEvents(events: CalendarEvent[]): CalendarEvent[] {
  const today = new Date().toISOString().split('T')[0];
  return getEventsForDate(events, today);
}

/**
 * Get upcoming events (sorted by date, limited)
 */
export function getUpcomingEvents(events: CalendarEvent[], limit = 10): CalendarEvent[] {
  const now = new Date().toISOString();
  return events
    .filter((e) => e.startAt >= now)
    .sort((a, b) => a.startAt.localeCompare(b.startAt))
    .slice(0, limit);
}

/**
 * Get reminders for a specific date (legacy compatibility)
 */
export function getRemindersForDate(reminders: Reminder[], date: string): Reminder[] {
  const targetDate = date.split('T')[0];
  return reminders.filter((r) => {
    const reminderDate = r.at.split('T')[0];
    return reminderDate === targetDate && !r.done;
  });
}

/**
 * Get all pending (not done) reminders (legacy compatibility)
 */
export function getPendingReminders(reminders: Reminder[]): Reminder[] {
  return reminders.filter((r) => !r.done);
}

/**
 * Get upcoming reminders (legacy compatibility)
 */
export function getUpcomingReminders(reminders: Reminder[]): Reminder[] {
  const now = new Date().toISOString();
  return reminders
    .filter((r) => !r.done && r.at >= now)
    .sort((a, b) => a.at.localeCompare(b.at))
    .slice(0, 10);
}

// =============================================================================
// NON-REACT ACCESS (for services and utilities)
// =============================================================================

/**
 * Read calendar events from the react-query cache.
 * For use in non-React code (services, utilities).
 * Returns empty array if cache is empty.
 */
export function getCalendarEventsFromCache(): CalendarEvent[] {
  return queryClient.getQueryData<CalendarEvent[]>(queryKeys.calendar.events()) ?? [];
}

/**
 * Read reminders from the react-query cache (legacy).
 * For use in non-React code (services, utilities).
 * Returns empty array if cache is empty.
 */
export function getRemindersFromCache(): Reminder[] {
  return queryClient.getQueryData<Reminder[]>(queryKeys.calendar.reminders()) ?? [];
}

// =============================================================================
// HELPERS (exported for iCal compatibility)
// =============================================================================

/**
 * Parse iCal RRULE string to RecurrenceRule object
 */
export function parseICalRecurrence(rrule: string): RecurrenceRule | undefined {
  try {
    const parts = rrule.split(';');
    const props: Record<string, string> = {};
    for (const part of parts) {
      const [key, value] = part.split('=');
      if (key && value) props[key] = value;
    }

    const freqMap: Record<string, RecurrenceFrequency> = {
      'DAILY': 'daily',
      'WEEKLY': 'weekly',
      'MONTHLY': 'monthly',
      'YEARLY': 'yearly',
    };

    const frequency = freqMap[props.FREQ];
    if (!frequency) return undefined;

    // Parse BYDAY (e.g., "MO,TU,WE" -> [1, 2, 3])
    const dayMap: Record<string, number> = {
      'SU': 0, 'MO': 1, 'TU': 2, 'WE': 3, 'TH': 4, 'FR': 5, 'SA': 6,
    };
    const byDay = props.BYDAY
      ? props.BYDAY.split(',').map(d => dayMap[d.replace(/[+-]?\d+/, '')]).filter(d => d !== undefined)
      : [];

    return {
      frequency,
      interval: props.INTERVAL ? parseInt(props.INTERVAL, 10) : 1,
      byDay,
      byMonthDay: props.BYMONTHDAY ? parseInt(props.BYMONTHDAY, 10) : undefined,
      count: props.COUNT ? parseInt(props.COUNT, 10) : undefined,
      until: props.UNTIL || undefined,
    };
  } catch {
    return undefined;
  }
}

/**
 * Convert RecurrenceRule to iCal RRULE string
 */
export function recurrenceToRRule(recurrence: RecurrenceRule): string {
  const freqMap: Record<RecurrenceFrequency, string> = {
    daily: 'DAILY',
    weekly: 'WEEKLY',
    monthly: 'MONTHLY',
    yearly: 'YEARLY',
  };

  const dayMap: Record<number, string> = {
    0: 'SU', 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR', 6: 'SA',
  };

  const parts = [`FREQ=${freqMap[recurrence.frequency]}`];

  if (recurrence.interval > 1) {
    parts.push(`INTERVAL=${recurrence.interval}`);
  }

  if (recurrence.byDay && recurrence.byDay.length > 0) {
    parts.push(`BYDAY=${recurrence.byDay.map(d => dayMap[d]).join(',')}`);
  }

  if (recurrence.byMonthDay) {
    parts.push(`BYMONTHDAY=${recurrence.byMonthDay}`);
  }

  if (recurrence.count) {
    parts.push(`COUNT=${recurrence.count}`);
  }

  if (recurrence.until) {
    parts.push(`UNTIL=${recurrence.until}`);
  }

  return parts.join(';');
}
